import React, { useState, useEffect } from 'react';
import { 
    Shield, ShieldCheck, ShieldAlert, Smartphone, Key, 
    Laptop, Trash2, RefreshCw, Copy, Check, Download, 
    AlertTriangle, X, Lock, CheckCircle, ChevronRight,
    Loader2, Info
} from 'lucide-react';
import { StorageService } from '../../services/storageService';
import { TwoFactorStatus, TrustedDeviceInfo } from '../../types';
import { useThemeTokens } from '../../hooks/useActiveTheme';

interface TwoFactorSecurityModalProps {
    isOpen: boolean;
    onClose: () => void;
    userEmail: string;
}

export const TwoFactorSecurityModal: React.FC<TwoFactorSecurityModalProps> = ({
    isOpen,
    onClose,
    userEmail
}) => {
    const tokens = useThemeTokens();
    const [activeTab, setActiveTab] = useState<'overview' | 'devices' | 'setup' | 'backupCodes'>('overview');
    const [status, setStatus] = useState<TwoFactorStatus | null>(null);
    const [devices, setDevices] = useState<TrustedDeviceInfo[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    // Setup Flow State
    const [setupData, setSetupData] = useState<{ secret: string; otpauthUrl: string; qrCodeDataUrl: string } | null>(null);
    const [verifyCode, setVerifyCode] = useState('');
    const [setupBackupCodes, setSetupBackupCodes] = useState<string[]>([]);
    const [hasCopiedCodes, setHasCopiedCodes] = useState(false);

    // Disable Flow State
    const [showDisableConfirm, setShowDisableConfirm] = useState(false);
    const [disablePassword, setDisablePassword] = useState('');
    const [disableCode, setDisableCode] = useState('');

    // Regenerate Backup Codes State
    const [showRegenModal, setShowRegenModal] = useState(false);
    const [regenCode, setRegenCode] = useState('');

    useEffect(() => {
        if (isOpen) {
            loadStatus();
            loadDevices();
        } else {
            // Reset modal states
            setError(null);
            setSuccessMessage(null);
            setVerifyCode('');
            setDisablePassword('');
            setDisableCode('');
            setShowDisableConfirm(false);
            setShowRegenModal(false);
        }
    }, [isOpen]);

    const loadStatus = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await StorageService.get2FAStatus();
            setStatus(data);
            if (!data.enabled) {
                setActiveTab('overview');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to load 2FA status');
        } finally {
            setIsLoading(false);
        }
    };

    const loadDevices = async () => {
        try {
            const data = await StorageService.getTrustedDevices();
            setDevices(data.devices || []);
        } catch (err: any) {
            console.warn('Failed to load trusted devices', err);
        }
    };

    const handleStartSetup = async () => {
        setActionLoading(true);
        setError(null);
        try {
            const data = await StorageService.setup2FA();
            setSetupData(data);
            setVerifyCode('');
            setActiveTab('setup');
        } catch (err: any) {
            setError(err.message || 'Failed to initialize 2FA setup');
        } finally {
            setActionLoading(false);
        }
    };

    const handleConfirmSetup = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!setupData || !verifyCode) return;
        setActionLoading(true);
        setError(null);
        try {
            const res = await StorageService.confirm2FASetup(verifyCode.trim(), setupData.secret);
            setSetupBackupCodes(res.backupCodes || []);
            setActiveTab('backupCodes');
            setSuccessMessage('Google Authenticator connected successfully!');
            await loadStatus();
            await loadDevices();
        } catch (err: any) {
            setError(err.message || 'Invalid code. Please make sure Google Authenticator is synced.');
        } finally {
            setActionLoading(false);
        }
    };

    const handleDisable2FA = async (e: React.FormEvent) => {
        e.preventDefault();
        setActionLoading(true);
        setError(null);
        try {
            await StorageService.disable2FA(disablePassword, disableCode);
            setShowDisableConfirm(false);
            setDisablePassword('');
            setDisableCode('');
            setSuccessMessage('Two-Factor Authentication has been disabled.');
            await loadStatus();
            setDevices([]);
            setActiveTab('overview');
        } catch (err: any) {
            setError(err.message || 'Failed to disable 2FA');
        } finally {
            setActionLoading(false);
        }
    };

    const handleRegenerateBackupCodes = async (e: React.FormEvent) => {
        e.preventDefault();
        setActionLoading(true);
        setError(null);
        try {
            const res = await StorageService.regenerateBackupCodes(regenCode.trim());
            setSetupBackupCodes(res.backupCodes || []);
            setShowRegenModal(false);
            setRegenCode('');
            setActiveTab('backupCodes');
            setSuccessMessage('New backup recovery codes generated. Old codes are now invalid.');
            await loadStatus();
        } catch (err: any) {
            setError(err.message || 'Invalid authenticator code');
        } finally {
            setActionLoading(false);
        }
    };

    const handleRevokeDevice = async (id: string) => {
        if (!window.confirm('Are you sure you want to revoke trust for this device? You will need to enter a 2FA code next time you sign in on it.')) return;
        try {
            await StorageService.revokeTrustedDevice(id);
            setSuccessMessage('Device trust revoked.');
            await loadDevices();
            await loadStatus();
        } catch (err: any) {
            setError(err.message || 'Failed to revoke device');
        }
    };

    const handleRevokeAllDevices = async () => {
        if (!window.confirm('Revoke all trusted devices? All active browsers will require a Google Authenticator code on next login.')) return;
        setActionLoading(true);
        try {
            await StorageService.revokeAllTrustedDevices();
            setSuccessMessage('All trusted devices have been revoked.');
            await loadDevices();
            await loadStatus();
        } catch (err: any) {
            setError(err.message || 'Failed to revoke all devices');
        } finally {
            setActionLoading(false);
        }
    };

    const copyBackupCodesToClipboard = () => {
        const text = `Diamond Nursing School - Emergency 2FA Backup Codes\nAccount: ${userEmail}\nGenerated: ${new Date().toLocaleString()}\n\n${setupBackupCodes.join('\n')}\n\nKeep these codes safe. Each code can be used once.`;
        navigator.clipboard.writeText(text);
        setHasCopiedCodes(true);
        setTimeout(() => setHasCopiedCodes(false), 3000);
    };

    const downloadBackupCodes = () => {
        const text = `Diamond Nursing School - Emergency 2FA Backup Codes\nAccount: ${userEmail}\nGenerated: ${new Date().toLocaleString()}\n\n${setupBackupCodes.join('\n')}\n\nKeep these codes safe. Each code can be used once.`;
        const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `diamond-nursing-2fa-backup-codes-${Date.now()}.txt`;
        link.click();
        URL.revokeObjectURL(url);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-fade-in">
            <div className={`relative w-full max-w-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] ${tokens.headerFont}`}>
                
                {/* Header */}
                <div className="p-6 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-50 dark:bg-slate-900/50">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-2xl bg-indigo-50 dark:bg-indigo-950/50 border border-indigo-200 dark:border-indigo-800 flex items-center justify-center text-indigo-600 dark:text-indigo-400">
                            <ShieldCheck size={22} />
                        </div>
                        <div>
                            <h2 className="text-lg font-black text-slate-900 dark:text-white flex items-center gap-2">
                                Two-Factor Authentication (2FA)
                                {status?.enabled && (
                                    <span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-400 border border-emerald-300 dark:border-emerald-800">
                                        Active
                                    </span>
                                )}
                            </h2>
                            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                                Google Authenticator & Trusted Device Management for Super Admins
                            </p>
                        </div>
                    </div>
                    <button 
                        onClick={onClose}
                        className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-white rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                        aria-label="Close modal"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Navigation Tabs (if 2FA is active) */}
                {status?.enabled && activeTab !== 'setup' && activeTab !== 'backupCodes' && (
                    <div className="flex border-b border-slate-200 dark:border-slate-800 px-6 bg-slate-50/50 dark:bg-slate-900/20">
                        <button
                            onClick={() => setActiveTab('overview')}
                            className={`py-3 px-4 text-xs font-black uppercase tracking-wider border-b-2 transition-colors cursor-pointer ${
                                activeTab === 'overview'
                                    ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400'
                                    : 'border-transparent text-slate-500 hover:text-slate-900 dark:hover:text-white'
                            }`}
                        >
                            Security Overview
                        </button>
                        <button
                            onClick={() => setActiveTab('devices')}
                            className={`py-3 px-4 text-xs font-black uppercase tracking-wider border-b-2 transition-colors flex items-center gap-2 cursor-pointer ${
                                activeTab === 'devices'
                                    ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400'
                                    : 'border-transparent text-slate-500 hover:text-slate-900 dark:hover:text-white'
                            }`}
                        >
                            <span>Trusted Devices</span>
                            {devices.length > 0 && (
                                <span className="px-1.5 py-0.2 text-[10px] font-black rounded-full bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                                    {devices.length}
                                </span>
                            )}
                        </button>
                    </div>
                )}

                {/* Notifications / Alerts */}
                {error && (
                    <div className="mx-6 mt-4 p-4 bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900/50 rounded-2xl flex items-center gap-3 text-rose-700 dark:text-rose-400 text-xs font-bold animate-pop">
                        <AlertTriangle size={18} className="shrink-0" />
                        <span>{error}</span>
                        <button onClick={() => setError(null)} className="ml-auto text-rose-400 hover:text-rose-600">
                            <X size={14} />
                        </button>
                    </div>
                )}

                {successMessage && (
                    <div className="mx-6 mt-4 p-4 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900/50 rounded-2xl flex items-center gap-3 text-emerald-700 dark:text-emerald-400 text-xs font-bold animate-pop">
                        <CheckCircle size={18} className="shrink-0" />
                        <span>{successMessage}</span>
                        <button onClick={() => setSuccessMessage(null)} className="ml-auto text-emerald-400 hover:text-emerald-600">
                            <X size={14} />
                        </button>
                    </div>
                )}

                {/* Modal Body */}
                <div className="p-6 overflow-y-auto space-y-6 flex-1">
                    {isLoading ? (
                        <div className="py-16 flex flex-col items-center justify-center space-y-3">
                            <Loader2 className="animate-spin text-indigo-600" size={32} />
                            <p className="text-xs font-bold text-slate-500">Checking 2FA security status...</p>
                        </div>
                    ) : activeTab === 'setup' && setupData ? (
                        /* STEP 1 & 2: SETUP FLOW */
                        <div className="space-y-6 animate-fade-in">
                            <div className="p-4 bg-indigo-50/50 dark:bg-indigo-950/20 border border-indigo-100 dark:border-indigo-900/30 rounded-2xl flex items-start gap-3">
                                <Smartphone className="text-indigo-600 dark:text-indigo-400 shrink-0 mt-0.5" size={20} />
                                <div className="text-xs space-y-1">
                                    <p className="font-black text-slate-900 dark:text-white">Step 1: Scan QR Code in Google Authenticator</p>
                                    <p className="text-slate-600 dark:text-slate-400">
                                        Open Google Authenticator on your mobile phone, tap "+" and select "Scan a QR code".
                                    </p>
                                </div>
                            </div>

                            <div className="flex flex-col sm:flex-row items-center gap-6 p-6 bg-slate-50 dark:bg-slate-950/50 border border-slate-200 dark:border-slate-800 rounded-3xl">
                                <div className="bg-white p-3 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 shrink-0">
                                    <img 
                                        src={setupData.qrCodeDataUrl} 
                                        alt="2FA QR Code" 
                                        className="w-44 h-44 object-contain"
                                    />
                                </div>
                                <div className="space-y-3 flex-1 text-center sm:text-left">
                                    <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Manual Entry Key</p>
                                    <p className="text-xs text-slate-600 dark:text-slate-400 font-medium">
                                        If your camera cannot scan the QR code, manually type this secret key into Google Authenticator:
                                    </p>
                                    <div className="p-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl font-mono text-xs font-black tracking-widest text-indigo-600 dark:text-indigo-400 break-all select-all flex items-center justify-between gap-2">
                                        <span>{setupData.secret}</span>
                                        <button 
                                            onClick={() => {
                                                navigator.clipboard.writeText(setupData.secret);
                                                setSuccessMessage('Secret key copied to clipboard');
                                            }}
                                            className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors text-slate-400 hover:text-slate-600 dark:hover:text-white"
                                            title="Copy secret key"
                                        >
                                            <Copy size={14} />
                                        </button>
                                    </div>
                                    <p className="text-[10px] text-slate-400">Account label: {userEmail}</p>
                                </div>
                            </div>

                            <form onSubmit={handleConfirmSetup} className="space-y-4 pt-2">
                                <div className="space-y-2">
                                    <label className="text-xs font-black text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                                        Step 2: Enter 6-Digit Authenticator Code
                                    </label>
                                    <input 
                                        type="text" 
                                        required
                                        inputMode="numeric"
                                        autoComplete="one-time-code"
                                        maxLength={6}
                                        value={verifyCode}
                                        onChange={e => setVerifyCode(e.target.value.replace(/\D/g, ''))}
                                        placeholder="000000"
                                        className="w-full text-center tracking-[0.4em] font-mono text-2xl font-black py-4 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all text-slate-900 dark:text-white"
                                    />
                                </div>

                                <div className="flex gap-3 pt-2">
                                    <button 
                                        type="button" 
                                        onClick={() => {
                                            setActiveTab('overview');
                                            setSetupData(null);
                                        }}
                                        className="flex-1 py-3 text-xs font-bold text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors cursor-pointer"
                                    >
                                        Cancel
                                    </button>
                                    <button 
                                        type="submit"
                                        disabled={verifyCode.length !== 6 || actionLoading}
                                        className={`flex-1 py-3 text-xs font-black uppercase tracking-wider shadow-lg flex items-center justify-center gap-2 rounded-xl active:scale-[0.98] disabled:opacity-50 cursor-pointer ${tokens.primaryBtn}`}
                                    >
                                        {actionLoading ? <Loader2 className="animate-spin" size={16} /> : <Check size={16} />}
                                        Verify & Activate 2FA
                                    </button>
                                </div>
                            </form>
                        </div>
                    ) : activeTab === 'backupCodes' ? (
                        /* STEP 3: BACKUP CODES DISPLAY */
                        <div className="space-y-6 animate-fade-in">
                            <div className="p-4 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/50 rounded-2xl flex items-start gap-3">
                                <AlertTriangle className="text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" size={20} />
                                <div className="text-xs space-y-1 text-amber-800 dark:text-amber-300">
                                    <p className="font-black">Save Your Emergency Backup Recovery Codes</p>
                                    <p className="font-medium text-amber-700 dark:text-amber-400">
                                        If you ever lose access to Google Authenticator or your phone, each of these 8 codes can be used once as your 2FA verification. We will NOT show them again.
                                    </p>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-4 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl">
                                {setupBackupCodes.map((code, idx) => (
                                    <div key={idx} className="p-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-center font-mono text-xs font-black tracking-wider text-slate-800 dark:text-slate-200">
                                        {code}
                                    </div>
                                ))}
                            </div>

                            <div className="flex flex-wrap gap-3">
                                <button
                                    type="button"
                                    onClick={copyBackupCodesToClipboard}
                                    className="flex-1 py-3 px-4 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-800 dark:text-white rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 cursor-pointer"
                                >
                                    {hasCopiedCodes ? <Check size={16} className="text-emerald-500" /> : <Copy size={16} />}
                                    <span>{hasCopiedCodes ? 'Copied to Clipboard!' : 'Copy All Codes'}</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={downloadBackupCodes}
                                    className="flex-1 py-3 px-4 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-800 dark:text-white rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 cursor-pointer"
                                >
                                    <Download size={16} />
                                    <span>Download .TXT File</span>
                                </button>
                            </div>

                            <div className="pt-2">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setActiveTab('overview');
                                        setSetupBackupCodes([]);
                                    }}
                                    className={`w-full py-4 text-xs font-black uppercase tracking-wider shadow-lg flex items-center justify-center gap-2 rounded-xl active:scale-[0.98] cursor-pointer ${tokens.primaryBtn}`}
                                >
                                    <CheckCircle size={16} />
                                    I Have Safely Stored My Backup Codes
                                </button>
                            </div>
                        </div>
                    ) : activeTab === 'devices' ? (
                        /* TRUSTED DEVICES TAB */
                        <div className="space-y-6 animate-fade-in">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="text-sm font-black text-slate-900 dark:text-white">Active Trusted Browsers</h3>
                                    <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                                        These devices bypass the 2FA prompt for up to 30 days.
                                    </p>
                                </div>
                                {devices.length > 0 && (
                                    <button
                                        onClick={handleRevokeAllDevices}
                                        disabled={actionLoading}
                                        className="px-3 py-1.5 text-[11px] font-bold text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/40 rounded-xl border border-rose-200 dark:border-rose-800 transition-colors cursor-pointer"
                                    >
                                        Revoke All Devices
                                    </button>
                                )}
                            </div>

                            {devices.length === 0 ? (
                                <div className="py-12 text-center space-y-2 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-2xl">
                                    <Laptop className="mx-auto text-slate-400" size={32} />
                                    <p className="text-xs font-bold text-slate-600 dark:text-slate-400">No trusted devices registered</p>
                                    <p className="text-[11px] text-slate-400">
                                        Check "Trust this device for 30 days" next time you log in to register a device.
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {devices.map(device => (
                                        <div 
                                            key={device.id}
                                            className="p-4 bg-slate-50 dark:bg-slate-950/60 border border-slate-200 dark:border-slate-800 rounded-2xl flex items-center justify-between gap-4"
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className="w-9 h-9 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 flex items-center justify-center text-slate-600 dark:text-slate-300 shrink-0">
                                                    <Laptop size={18} />
                                                </div>
                                                <div className="space-y-0.5">
                                                    <div className="flex items-center gap-2">
                                                        <p className="text-xs font-black text-slate-900 dark:text-white">
                                                            {device.deviceName || `${device.browser} on ${device.os}`}
                                                        </p>
                                                        {device.isCurrentDevice && (
                                                            <span className="px-1.5 py-0.5 text-[9px] font-black uppercase rounded bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300">
                                                                Current Session
                                                            </span>
                                                        )}
                                                    </div>
                                                    <p className="text-[10px] text-slate-500 font-medium">
                                                        IP: {device.ipAddress} • Last active: {device.lastUsedAt ? new Date(device.lastUsedAt).toLocaleDateString() : 'Recent'}
                                                    </p>
                                                </div>
                                            </div>

                                            <button
                                                onClick={() => handleRevokeDevice(device.id)}
                                                className="p-2 text-slate-400 hover:text-rose-600 dark:hover:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/30 rounded-xl transition-colors cursor-pointer"
                                                title="Revoke trust for this device"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ) : (
                        /* OVERVIEW TAB */
                        <div className="space-y-6">
                            {status?.enabled ? (
                                <>
                                    {/* Active State Card */}
                                    <div className="p-6 bg-emerald-50/50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800/40 rounded-3xl space-y-4">
                                        <div className="flex items-start justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className="w-12 h-12 rounded-2xl bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0">
                                                    <ShieldCheck size={26} />
                                                </div>
                                                <div>
                                                    <h3 className="text-sm font-black text-slate-900 dark:text-white">
                                                        Two-Factor Authentication is Enabled
                                                    </h3>
                                                    <p className="text-xs text-slate-600 dark:text-slate-400 font-medium">
                                                        Protected with Google Authenticator (TOTP)
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                                            <div className="p-3 bg-white dark:bg-slate-900 border border-emerald-200/50 dark:border-emerald-800/30 rounded-2xl">
                                                <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">Trusted Browsers</p>
                                                <p className="text-lg font-black text-slate-900 dark:text-white">{devices.length} Active</p>
                                                <button 
                                                    onClick={() => setActiveTab('devices')}
                                                    className="text-[11px] font-bold text-indigo-600 dark:text-indigo-400 hover:underline mt-1 inline-flex items-center gap-1 cursor-pointer"
                                                >
                                                    Manage devices <ChevronRight size={12} />
                                                </button>
                                            </div>
                                            <div className="p-3 bg-white dark:bg-slate-900 border border-emerald-200/50 dark:border-emerald-800/30 rounded-2xl">
                                                <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">Backup Recovery Codes</p>
                                                <p className="text-lg font-black text-slate-900 dark:text-white">{status.backupCodesRemaining} Remaining</p>
                                                <button 
                                                    onClick={() => setShowRegenModal(true)}
                                                    className="text-[11px] font-bold text-indigo-600 dark:text-indigo-400 hover:underline mt-1 inline-flex items-center gap-1 cursor-pointer"
                                                >
                                                    Regenerate codes <RefreshCw size={12} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="space-y-3 pt-2">
                                        <button
                                            type="button"
                                            onClick={handleStartSetup}
                                            disabled={actionLoading}
                                            className="w-full py-3 px-4 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-800 dark:text-white rounded-2xl text-xs font-bold transition-all flex items-center justify-center gap-2 cursor-pointer"
                                        >
                                            <RefreshCw size={15} />
                                            Re-scan Google Authenticator QR Code
                                        </button>

                                        {!showDisableConfirm ? (
                                            <button
                                                type="button"
                                                onClick={() => setShowDisableConfirm(true)}
                                                className="w-full py-3 px-4 text-xs font-bold text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/20 rounded-2xl border border-rose-200 dark:border-rose-900/50 transition-colors cursor-pointer"
                                            >
                                                Disable Two-Factor Authentication
                                            </button>
                                        ) : (
                                            <form onSubmit={handleDisable2FA} className="p-4 bg-rose-50/50 dark:bg-rose-950/20 border border-rose-200 dark:border-rose-900/40 rounded-2xl space-y-3 animate-fade-in">
                                                <p className="text-xs font-black text-rose-700 dark:text-rose-400">
                                                    Confirm 2FA Deactivation
                                                </p>
                                                <p className="text-[11px] text-slate-600 dark:text-slate-400">
                                                    Enter your admin password or a Google Authenticator code to confirm:
                                                </p>
                                                <div className="space-y-2">
                                                    <input 
                                                        type="password"
                                                        placeholder="Admin password"
                                                        value={disablePassword}
                                                        onChange={e => setDisablePassword(e.target.value)}
                                                        className="w-full px-3 py-2 text-xs bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl outline-none"
                                                    />
                                                    <div className="text-center text-[10px] text-slate-400 font-bold">OR</div>
                                                    <input 
                                                        type="text"
                                                        inputMode="numeric"
                                                        maxLength={6}
                                                        placeholder="6-digit authenticator code"
                                                        value={disableCode}
                                                        onChange={e => setDisableCode(e.target.value)}
                                                        className="w-full px-3 py-2 text-xs bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl outline-none text-center font-mono tracking-widest"
                                                    />
                                                </div>
                                                <div className="flex gap-2">
                                                    <button 
                                                        type="button" 
                                                        onClick={() => setShowDisableConfirm(false)}
                                                        className="flex-1 py-2 text-xs font-bold text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl cursor-pointer"
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button 
                                                        type="submit"
                                                        disabled={(!disablePassword && !disableCode) || actionLoading}
                                                        className="flex-1 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-xs font-black transition-colors disabled:opacity-50 cursor-pointer"
                                                    >
                                                        {actionLoading ? 'Disabling...' : 'Confirm Disable'}
                                                    </button>
                                                </div>
                                            </form>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <>
                                    {/* Inactive State - Explanation & Enrollment Trigger */}
                                    <div className="p-8 text-center space-y-4 border border-slate-200 dark:border-slate-800 rounded-3xl bg-slate-50/50 dark:bg-slate-950/40">
                                        <div className="w-16 h-16 mx-auto rounded-3xl bg-indigo-50 dark:bg-indigo-950/60 border border-indigo-200 dark:border-indigo-800 flex items-center justify-center text-indigo-600 dark:text-indigo-400">
                                            <Shield size={32} />
                                        </div>
                                        <div className="space-y-1 max-w-md mx-auto">
                                            <h3 className="text-base font-black text-slate-900 dark:text-white">
                                                Protect Your Super Admin Account
                                            </h3>
                                            <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed font-medium">
                                                Add an extra layer of clinical defense. Each time you log in from a new browser, you will enter a 6-digit verification code from Google Authenticator.
                                            </p>
                                        </div>

                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-left max-w-lg mx-auto pt-2">
                                            <div className="p-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl space-y-1">
                                                <Smartphone size={16} className="text-indigo-600 dark:text-indigo-400" />
                                                <p className="text-[11px] font-black text-slate-800 dark:text-slate-200">Google Auth</p>
                                                <p className="text-[10px] text-slate-500">Standard RFC 6238 TOTP compatible.</p>
                                            </div>
                                            <div className="p-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl space-y-1">
                                                <Laptop size={16} className="text-emerald-600 dark:text-emerald-400" />
                                                <p className="text-[11px] font-black text-slate-800 dark:text-slate-200">Trusted Browsers</p>
                                                <p className="text-[10px] text-slate-500">Remember your personal PC for 30 days.</p>
                                            </div>
                                            <div className="p-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl space-y-1">
                                                <Key size={16} className="text-amber-600 dark:text-amber-400" />
                                                <p className="text-[11px] font-black text-slate-800 dark:text-slate-200">Emergency Backup</p>
                                                <p className="text-[10px] text-slate-500">Single-use codes if you misplace your phone.</p>
                                            </div>
                                        </div>

                                        <div className="pt-4 max-w-sm mx-auto">
                                            <button
                                                type="button"
                                                onClick={handleStartSetup}
                                                disabled={actionLoading}
                                                className={`w-full py-4 text-xs font-black uppercase tracking-wider shadow-xl flex items-center justify-center gap-2.5 rounded-2xl active:scale-[0.98] cursor-pointer ${tokens.primaryBtn}`}
                                            >
                                                {actionLoading ? <Loader2 className="animate-spin" size={16} /> : <ShieldCheck size={18} />}
                                                Enable Google Authenticator 2FA
                                            </button>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>

                {/* Regenerate Backup Codes Dialog */}
                {showRegenModal && (
                    <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm z-20 flex items-center justify-center p-6 animate-fade-in">
                        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 max-w-md w-full shadow-2xl space-y-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2.5 bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 rounded-xl">
                                    <Key size={20} />
                                </div>
                                <div>
                                    <h3 className="text-sm font-black text-slate-900 dark:text-white">Regenerate Backup Codes</h3>
                                    <p className="text-[11px] text-slate-500">This will immediately invalidate all existing backup codes.</p>
                                </div>
                            </div>

                            <form onSubmit={handleRegenerateBackupCodes} className="space-y-4">
                                <div className="space-y-1">
                                    <label className="text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                                        Enter 6-Digit Google Authenticator Code
                                    </label>
                                    <input 
                                        type="text" 
                                        required
                                        inputMode="numeric"
                                        maxLength={6}
                                        value={regenCode}
                                        onChange={e => setRegenCode(e.target.value.replace(/\D/g, ''))}
                                        placeholder="000000"
                                        className="w-full text-center tracking-[0.4em] font-mono text-xl font-black py-3 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl outline-none text-slate-900 dark:text-white"
                                    />
                                </div>

                                <div className="flex gap-2">
                                    <button 
                                        type="button" 
                                        onClick={() => {
                                            setShowRegenModal(false);
                                            setRegenCode('');
                                        }}
                                        className="flex-1 py-2.5 text-xs font-bold text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl cursor-pointer"
                                    >
                                        Cancel
                                    </button>
                                    <button 
                                        type="submit"
                                        disabled={regenCode.length !== 6 || actionLoading}
                                        className={`flex-1 py-2.5 text-xs font-black uppercase tracking-wider shadow flex items-center justify-center gap-2 rounded-xl active:scale-[0.98] disabled:opacity-50 cursor-pointer ${tokens.primaryBtn}`}
                                    >
                                        {actionLoading ? <Loader2 className="animate-spin" size={16} /> : <RefreshCw size={16} />}
                                        Generate New Codes
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
